import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Typed-out display of incoming AI text for a calm Arabic typing feel.
 */
function TypedText({ text, speed = 18, onDone }) {
  const [shown, setShown] = useState("");
  useEffect(() => {
    let i = 0;
    setShown("");
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        onDone && onDone();
      }
    }, speed);
    return () => clearInterval(id);
  }, [text, speed, onDone]);
  return <span>{shown}</span>;
}

export default function AIChat({ lesson, prompt }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: `أهلًا بك! أنا معلّمك في درس «${lesson.title}». ${prompt || ""} ابدأ متى تشاء.`,
      typed: true,
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [improving, setImproving] = useState(false);
  const streamRef = useRef(null);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text, typed: true }]);
    setBusy(true);
    try {
      const res = await api.chat({
        lesson_id: lesson.id,
        session_id: sessionId,
        message: text,
      });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "ai", text: res.reply, typed: false }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "ai",
          text: "تعذّر الاتصال بالمعلّم الذكي الآن. حاول مجدّدًا بعد قليل.",
          typed: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const improveLast = async () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) {
      setMessages((m) => [
        ...m,
        { role: "ai", text: "اكتب جملة أولًا ثم اضغط «حسّن أسلوبي» لأقترح صياغة أبلغ.", typed: true },
      ]);
      return;
    }
    setImproving(true);
    try {
      const res = await api.improve({
        lesson_id: lesson.id,
        text: lastUser.text,
      });
      setMessages((m) => [
        ...m,
        {
          role: "ai",
          text: `✨ صياغة أبلغ:\n«${res.improved}»\n\nملاحظات: ${res.notes}`,
          typed: false,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "ai", text: "تعذّر التحسين الآن، حاول لاحقًا.", typed: true },
      ]);
    } finally {
      setImproving(false);
    }
  };

  return (
    <div className="chat-wrap" data-testid="ai-chat">
      <div className="chat-stream" ref={streamRef}>
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`bubble ${m.role}`}
            data-testid={`chat-bubble-${m.role}`}
          >
            {m.typed ? (
              <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>
            ) : (
              <TypedText
                text={m.text}
                onDone={() =>
                  setMessages((prev) =>
                    prev.map((p, i) => (i === idx ? { ...p, typed: true } : p))
                  )
                }
              />
            )}
          </div>
        ))}
        {busy && (
          <div className="bubble ai" data-testid="chat-typing">
            <span className="typing">
              <i /><i /><i />
            </span>
          </div>
        )}
      </div>

      <div className="chat-input">
        <input
          data-testid="chat-input"
          value={input}
          placeholder="اكتب ردّك هنا..."
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") send();
          }}
        />
        <button
          data-testid="chat-send-btn"
          className="btn btn-primary"
          onClick={send}
          disabled={busy}
        >
          إرسال
        </button>
      </div>
      <div className="row" style={{ marginTop: 4 }}>
        <button
          data-testid="improve-style-btn"
          className="btn btn-gold"
          onClick={improveLast}
          disabled={improving || busy}
        >
          {improving ? "يحسّن..." : "✨ حسّن أسلوبي"}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          يأخذ آخر ردّ لك ويعيد صياغته بشكل أبلغ.
        </span>
      </div>
    </div>
  );
}
